<script lang="ts">
    import type { DateTime } from 'luxon'

    import { gettext as _, resolve } from 'hydria/services/gettext'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import type { UserStatus } from './user'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    interface Props {
        userId: string
        status: UserStatus
        reload?: number
    }

    let { userId, status, reload = 1 }: Props = $props()
    let logStatus: 'loading' | 'ready' | 'error' = $state('loading')
    let loadingUserId: string | undefined = undefined

    interface Log {
        id: string
        revision: number
        user_id: string
        stamp: DateTime
        action: string
        source_ip: string
        source_user_id: string
        comment_i18n: {
            text: string
            params: Object
        }
    }
    let log: Log[] = $state([])

    async function loadLog(userId: string) {
        let r: SimpleFetchResult

        logStatus = 'loading'
        loadingUserId = userId
        try {
            r = await simpleFetch('GET', `/json/users/1/${userId}/log`)
        } catch (e) {
            console.error('Failed to load log:', e)
            logStatus = 'error'
            return []
        }

        if (loadingUserId !== userId) return []

        const llog: Log[] = []

        for (const entry of r.data.items as Log[]) {
            if (entry.stamp) entry.stamp = toLuxon(entry.stamp)
            llog.push(entry)
        }
        log = llog
        logStatus = 'ready'
    }

    $effect(() => {
        if (reload > 0 && userId != null) loadLog(userId)
    })
</script>

<div class="card">
    <div class="card-header text-bg-secondary">{$_('User modification log')}</div>

    <div class="">
        <table class="table mb-0 table-sm table-hover">
            <thead>
                <tr>
                    <th>{$_('Stamp')}</th>
                    <th>{$_('Action')}</th>
                    <th>{$_('Comment')}</th>
                    <th>{$_('IP')}</th>
                    <th>{$_('User ID')}</th>
                </tr>
            </thead>
            <tbody>
                {#if logStatus == 'loading'}
                    <tr>
                        <td colspan={5}><LoadingPattern pattern={['x']} /></td>
                    </tr>
                    <tr>
                        <td colspan={5}><LoadingPattern pattern={['x']} /></td>
                    </tr>
                    <tr>
                        <td colspan={5}><LoadingPattern pattern={['x']} /></td>
                    </tr>
                {:else if !log.length}
                    <tr>
                        <td colspan={5}>Nothing to report</td>
                    </tr>
                {:else}
                    {#each log as entry}
                        <tr>
                            <td><Date value={entry.stamp} format="h-table-s" /></td>
                            <td>{entry.action}</td>
                            <td>{$resolve(entry.comment_i18n.text, entry.comment_i18n.params)}</td>
                            <td>{entry.source_ip}</td>
                            <td>
                                {#if entry.source_user_id == userId}
                                    {$_('The user himself')}
                                {:else}
                                    <a use:sanchor href={spath(`/admin-user/${entry.source_user_id}`)}>{entry.source_user_id}</a>
                                {/if}
                            </td>
                        </tr>
                    {/each}
                {/if}
            </tbody>
        </table>
    </div>
</div>
